import { useState } from 'react';
import FaqEntry from './FaqEntry.tsx';
import { PRODUCT_SUBSCRIPTION_TYPE, PRODUCT_TITLE } from './constants.ts';
import {
    FAQ_ENTRIES,
    FAQ_HEADING,
    FAQ_LEDE,
    PRODUCT_NAME_PLACEHOLDER,
    type FaqBlock,
    type FaqEntry as Entry
} from './content.ts';

const withProductName = (text: string): string => text.split(PRODUCT_NAME_PLACEHOLDER).join(PRODUCT_TITLE);

const resolveBlock = (block: FaqBlock): FaqBlock => (
    block.kind === 'paragraph' ? { kind: 'paragraph', text: withProductName(block.text) } : block
);

/** Picks the copy written for this product's lead model, falling back to the shared answer. */
const resolveAnswer = (entry: Entry): FaqBlock[] => {
    const override = entry.answerOverrides?.[PRODUCT_SUBSCRIPTION_TYPE];
    return (override ?? entry.answer).map(resolveBlock);
};

const FaqSection = (): JSX.Element => {
    const [openQuestions, setOpenQuestions] = useState<ReadonlySet<number>>(new Set([0]));

    const toggle = (index: number): void => {
        setOpenQuestions((previous) => {
            const next = new Set(previous);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    return (
        <section id="faq" className="w-full bg-black text-white py-20 md:py-32 px-6 md:px-10">
            <div className="container m-auto grid gap-12 md:grid-cols-12 md:gap-20">
                <div className="md:col-span-5 md:sticky md:top-12 md:self-start flex flex-col gap-5">
                    <p className="text-blue-600 font-bold text-sm uppercase tracking-widest">FAQ</p>
                    <h2 className="text-4xl md:text-5xl font-bold leading-tight">{FAQ_HEADING}</h2>
                    <p className="text-lg opacity-70 max-w-md">{FAQ_LEDE}</p>
                    <div className="flex flex-col items-start gap-3 mt-2">
                        <p className="opacity-50">Still have a question?</p>
                        <div className="flex flex-wrap gap-3">
                            <a href="/register">
                                <button className="bg-blue-600 border border-blue-600 text-white hover:bg-opacity-90 px-8 py-3 rounded transition">
                                    Let&apos;s Get Started!
                                </button>
                            </a>
                            <a href="/contact">
                                <button className="bg-transparent text-blue-600 border border-blue-600 hover:text-white hover:bg-blue-600 px-8 py-3 rounded transition">
                                    Contact Us
                                </button>
                            </a>
                        </div>
                    </div>
                </div>

                <div className="md:col-span-7 border-t border-white/10">
                    {FAQ_ENTRIES.map((entry, index) => (
                        <FaqEntry
                            key={entry.question}
                            id={`faq-${index}`}
                            question={withProductName(entry.question)}
                            answer={resolveAnswer(entry)}
                            open={openQuestions.has(index)}
                            onToggle={() => { toggle(index); }}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FaqSection;
